import { Reveal } from "@/components/Reveal";

interface ImageGridProps {
  images: string[];
  columns?: 2 | 3 | 4;
  height?: string;
  className?: string;
  layout?: "grid" | "gallery" | "collage";
  rounded?: boolean;
}

export function ImageGrid({
  images,
  columns = 3,
  height = "md:h-[420px]",
  className = "",
  layout = "grid",
  rounded = false,
}: ImageGridProps) {
  const gridCols = {
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
  };

  if (layout === "collage" && images.length >= 5) {
    return (
      <Reveal
        stagger
        className={`overflow-hidden ${rounded ? "border border-white/10 rounded-3xl" : ""} grid md:grid-cols-3 ${className}`}
      >
        {/* Column 1: Tall Single Image */}
        <div className="group relative h-[300px] md:h-[600px] overflow-hidden border-b md:border-b-0 md:border-r border-accent/40">
          <img
            src={images[0]}
            alt="Manufacturing process"
            className="h-full w-full object-cover transition-transform duration-[1800ms] group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/10" />
        </div>

        {/* Column 2: 2 Stacked Images */}
        <div className="flex flex-col border-b md:border-b-0 md:border-r border-accent/40">
          <div className="group relative h-[200px] md:h-[300px] overflow-hidden border-b border-accent/40">
            <img
              src={images[1]}
              alt="CAD design"
              className="h-full w-full object-cover transition-transform duration-[1800ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/10" />
          </div>
          <div className="group relative h-[200px] md:h-[300px] overflow-hidden">
            <img
              src={images[2]}
              alt="Lathe chuck"
              className="h-full w-full object-cover transition-transform duration-[1800ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/10" />
          </div>
        </div>

        {/* Column 3: 2 Stacked Images */}
        <div className="flex flex-col">
          <div className="group relative h-[200px] md:h-[300px] overflow-hidden border-b border-accent/40">
            <img
              src={images[3]}
              alt="Finished hardware"
              className="h-full w-full object-cover transition-transform duration-[1800ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/10" />
          </div>
          <div className="group relative h-[200px] md:h-[300px] overflow-hidden">
            <img
              src={images[4]}
              alt="Testing setup"
              className="h-full w-full object-cover transition-transform duration-[1800ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/10" />
          </div>
        </div>
      </Reveal>
    );
  }

  if (layout === "gallery" && images.length >= 3) {
    return (
      <Reveal
        stagger
        className={`overflow-hidden ${rounded ? "border border-white/10 rounded-3xl" : ""} ${className}`}
      >
        <div className="group relative h-[320px] md:h-[520px] overflow-hidden border-b border-accent/40">
          <img
            src={images[0]}
            alt="Featured"
            className="h-full w-full object-cover transition-transform duration-[1800ms] group-hover:scale-105"
          />

          <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/10" />
        </div>

        <div className="grid md:grid-cols-2">
          <div className="group relative h-[220px] md:h-[320px] overflow-hidden border-r border-accent/40">
            <img
              src={images[1]}
              alt="Secondary 1"
              className="h-full w-full object-cover transition-transform duration-[1800ms] group-hover:scale-105"
            />

            <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/10" />
          </div>

          <div className="group relative h-[220px] md:h-[320px] overflow-hidden">
            <img
              src={images[2]}
              alt="Secondary 2"
              className="h-full w-full object-cover transition-transform duration-[1800ms] group-hover:scale-105"
            />

            <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/10" />
          </div>
        </div>
      </Reveal>
    );
  }
  return (
    <Reveal
      stagger
      className={`overflow-hidden ${rounded ? "border border-white/10 rounded-3xl" : ""} md:grid ${gridCols[columns]} ${className}`}
    >
      {images.map((img, idx) => (
        <div
          key={idx}
          className={`group relative h-[220px] overflow-hidden border-r border-accent/40 last:border-r-0 ${height}`}
        >
          <img
            src={img}
            alt={`Image ${idx + 1}`}
            loading="lazy"
            width={700}
            height={500}
            className="h-full w-full object-cover transition-transform duration-[1800ms] group-hover:scale-105"
          />

          <div className="absolute inset-0 bg-black/25 transition-colors duration-500 group-hover:bg-black/10" />
        </div>
      ))}
    </Reveal>
  );
}
